import { getStrikeStepForSymbol } from '../config/constants'
import type { AnalysisMode, AnalysisResult, MarketData, MarketSnapshot } from '../types'
import { getSortedOptions } from './analysis-utils'
import { performMarketCalculations } from '@options-oracle/core-logic'
import type { IndicatorLevels } from './technical-indicators'
import { IndexSymbol } from '@options-oracle/types'
import { extractSnapshotOptions } from './snapshot-search'

// --- Types ---

export interface ArbitrageSignal {
    type: 'DISCOUNT_CE' | 'DISCOUNT_PE' | 'SYNTHETIC_SKEW' | 'VERTICAL_CREDIT'
    message: string
    profitEst: number
    strike: number
}

export interface TrendFactors {
    shiftTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    varianceTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    countTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    pcrTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    vixTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    resistanceTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    supportTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    shiftOldTrend: 'UP' | 'DOWN' | 'SIDEWAYS'
    rsiDivergence: number // CE RSI - PE RSI
    premiumDecayRisk: 'HIGH' | 'MEDIUM' | 'LOW'
    gexRegime: 'STABLE' | 'VOLATILE' | 'NEUTRAL'
}

export interface TradeManagement {
    action: 'HOLD' | 'EXIT' | 'CAUTION' | 'WAIT'
    stopLoss: string
    target: string
    warning?: string
}

export interface TradeIntelligenceInput {
    marketData: MarketData
    analysis: (AnalysisResult & { variance: any }) | null
    mode: AnalysisMode
    history?: MarketSnapshot[]
    rsiData?: { ceRsi: number; peRsi: number }
    ceIndicators?: IndicatorLevels
    peIndicators?: IndicatorLevels
    ceStrikeLtp?: number
    peStrikeLtp?: number
}

export interface TradeIntelligenceResult {
    score: number // -100 to +100 range for V5.0
    signalType: 'BULLISH' | 'BEARISH' | 'NEUTRAL'
    signalSubType:
        | 'BREAKOUT'
        | 'BREAKDOWN'
        | 'REVERSAL_SHARP'
        | 'REVERSAL_SLOW'
        | 'TREND_CONTINUATION'
        | 'RANGE_BOUND'
        | 'BULL_TRAP'
        | 'BEAR_TRAP'
        | 'LIQUIDITY_SWEEP'
        | 'BASE_TIME_DIVERGENCE'
        | 'DUAL_MIGRATION'
        | 'COUNT_SINGULARITY'
        | 'VOLUME_OI_CONFLICT'
    signalStrength: 'STRONG' | 'MODERATE' | 'WEAK'
    conviction: number
    setup: any
    management: TradeManagement
    levels: { s1: number; s2: number; r1: number; r2: number; shift: number }
    thesisPoints: { type: 'bull' | 'bear' | 'neutral' | 'risk'; text: string }[]
    compiledThesis: string
    metrics: { variance: number; pcr: number; vix: number; totalCount: number }
    trendFactors: TrendFactors
    strikeLowData: {
        score: number
        netScore: number
        peBrokenCount: number
        ceAboveCount: number
        ceBrokenCount: number
        peAboveCount: number
        peLowsCount: number
        ceLowsCount: number
    }
    gexData?: {
        highestPosGexStrike: number
        highestNegGexStrike: number
        isSpotBelowZeroGamma: boolean
        zeroGammaLevel: number
    }
    arbitrageSignals?: ArbitrageSignal[]
}

// --- Trend Calculation Utilities ---

/**
 * Calculates whether a time series is trending UP, DOWN, or SIDEWAYS.
 * Uses Simple Moving Average comparison of first vs last 3 points.
 */
function calculateTrend(
    series: number[],
    thresholdPercent: number = 0.5, // Default threshold
): 'UP' | 'DOWN' | 'SIDEWAYS' {
    if (series.length < 3) return 'SIDEWAYS'

    // Use exponential weighting for recent data? No, keep it robust with simple averaging for now.
    const firstAvg = series.slice(0, 3).reduce((a, b) => a + b, 0) / 3
    const lastAvg = series.slice(-3).reduce((a, b) => a + b, 0) / 3

    // Handle near-zero values safely
    const denom = Math.abs(firstAvg) < 0.001 ? 1 : Math.abs(firstAvg)
    const changePercent = ((lastAvg - firstAvg) / denom) * 100

    if (changePercent > thresholdPercent) return 'UP'
    if (changePercent < -thresholdPercent) return 'DOWN'
    return 'SIDEWAYS'
}

/**
 * Extract metric series from history snapshots.
 * Enhanced V5.2: Properly reads from TransformedSnapshot structure with all 7 metrics.
 */
function extractMetricSeries(
    history: MarketSnapshot[] | undefined,
    metricKey: 'shift' | 'variance' | 'totalCount' | 'pcr' | 'vix' | 'resistance' | 'support' | 'shiftOld',
): number[] {
    if (!history || history.length < 3) return []

    // OPTIMIZATION: We only need the oldest elements and newest elements for trend calculation.
    // Processing all 400+ intraday snapshots synchronously freezes the main thread.
    // We will extract up to 10 from the beginning and 10 from the end to account for potential 0-filtering.
    const indicesToProcess = new Set<number>()
    for (let i = 0; i < 10 && i < history.length; i++) indicesToProcess.add(i)
    for (let i = history.length - 1; i >= 0 && i >= history.length - 10; i--) indicesToProcess.add(i)

    const sortedIndices = Array.from(indicesToProcess).sort((a, b) => a - b)
    const sampledHistory = sortedIndices.map((i) => history[i])

    const series = sampledHistory.map((snap: any) => {
        // 1. Flat structure from useAnalysisHistory (Preferred)
        if (snap.resistanceStrike !== undefined || snap.finalVariance !== undefined) {
            if (metricKey === 'shift') return snap.shift || 0
            if (metricKey === 'shiftOld') return snap.shiftOld || snap.shift || 0
            if (metricKey === 'variance')
                return snap.finalVariance !== undefined ? snap.finalVariance : snap.variance?.finalVariance || 0
            if (metricKey === 'totalCount')
                return snap.totalCount !== undefined ? snap.totalCount : snap.variance?.totalCount || 0
            if (metricKey === 'resistance') return snap.resistanceStrike || 0
            if (metricKey === 'support') return snap.supportStrike || 0
            if (metricKey === 'pcr') return snap.pcr !== undefined ? snap.pcr : 1
            if (metricKey === 'vix')
                return snap.vix !== undefined
                    ? snap.vix
                    : snap.data?.vix || snap.data?.strategyChainData?.marketMeta?.vix || 0
        }

        // 2. Embedded analysis or raw data
        const analysis = snap.analysis || snap.data?.analysis
        if (analysis) {
            if (metricKey === 'shift') return analysis.shift || 0
            if (metricKey === 'shiftOld') return analysis.shiftOld || analysis.shift || 0
            if (metricKey === 'variance') return analysis.variance?.finalVariance || 0
            if (metricKey === 'totalCount') return analysis.variance?.totalCount || analysis.totalCount || 0
            if (metricKey === 'resistance') return analysis.resistanceStrike || 0
            if (metricKey === 'support') return analysis.supportStrike || 0
            if (metricKey === 'pcr') return analysis.pcr !== undefined ? analysis.pcr : snap.pcr || 1
        }

        // 3. Fallbacks for raw unanalyzed snapshots (re-calculation)
        if (metricKey === 'shift') return computeShiftFromRaw(snap)
        if (metricKey === 'shiftOld') return computeShiftFromRaw(snap)
        if (metricKey === 'variance') return 0
        if (metricKey === 'totalCount') return 0
        if (metricKey === 'pcr') return snap.pcr !== undefined ? snap.pcr : 1
        if (metricKey === 'vix')
            return snap.vix !== undefined
                ? snap.vix
                : snap.data?.vix || snap.data?.strategyChainData?.marketMeta?.vix || 0
        if (metricKey === 'resistance') return computeResistanceFromRaw(snap)
        if (metricKey === 'support') return computeSupportFromRaw(snap)

        return 0
    })

    // Keep all values including 0 for variance, totalCount, and vix
    const keepZeros = ['variance', 'totalCount', 'vix']
    return keepZeros.includes(metricKey) ? series : series.filter((v: number) => v !== 0)
}

// Helper: Compute shift from raw strikeMap
function computeShiftFromRaw(snap: any): number {
    const symbol = snap.symbol || IndexSymbol.NIFTY
    const rawData = snap?.data?.strategyChainData || snap
    const strikeMap = rawData?.strikeMap || {}
    const strikes = Object.keys(strikeMap)
        .map(Number)
        .filter((n) => !isNaN(n))
    if (strikes.length === 0) return 0

    const options = strikes.map((s) => ({
        strike: s,
        callOpenInterest: strikeMap[s]?.callOptionData?.marketData?.oi || 0,
        putOpenInterest: strikeMap[s]?.putOptionData?.marketData?.oi || 0,
        callVolume: strikeMap[s]?.callOptionData?.marketData?.v || 0,
        putVolume: strikeMap[s]?.putOptionData?.marketData?.v || 0,
    }))

    const res = performMarketCalculations(options as any, snap.spotPrice || snap.indexLtp || 0, {
        mode: (snap.mode as any) || 'OI',
        range: 10,
        symbol: symbol as any,
    })
    return res.shift
}

// Helper: Compute resistance from raw strikeMap
function computeResistanceFromRaw(snap: any): number {
    const symbol = snap.symbol || IndexSymbol.NIFTY
    const rawData = snap?.data?.strategyChainData || snap
    const strikeMap = rawData?.strikeMap || {}
    const strikes = Object.keys(strikeMap)
        .map(Number)
        .filter((n) => !isNaN(n))
    if (strikes.length === 0) return 0

    const options = strikes.map((s) => ({
        strike: s,
        callOpenInterest: strikeMap[s]?.callOptionData?.marketData?.oi || 0,
        putOpenInterest: strikeMap[s]?.putOptionData?.marketData?.oi || 0,
        callVolume: strikeMap[s]?.callOptionData?.marketData?.v || 0,
        putVolume: strikeMap[s]?.putOptionData?.marketData?.v || 0,
    }))

    const res = performMarketCalculations(options as any, snap.spotPrice || snap.indexLtp || 0, {
        mode: (snap.mode as any) || 'OI',
        range: 10,
        symbol: symbol as any,
    })
    return res.resistanceStrike
}

// Helper: Compute support from raw strikeMap
function computeSupportFromRaw(snap: any): number {
    const symbol = snap.symbol || IndexSymbol.NIFTY
    const rawData = snap?.data?.strategyChainData || snap
    const strikeMap = rawData?.strikeMap || {}
    const strikes = Object.keys(strikeMap)
        .map(Number)
        .filter((n) => !isNaN(n))
    if (strikes.length === 0) return 0

    const options = strikes.map((s) => ({
        strike: s,
        callOpenInterest: strikeMap[s]?.callOptionData?.marketData?.oi || 0,
        putOpenInterest: strikeMap[s]?.putOptionData?.marketData?.oi || 0,
        callVolume: strikeMap[s]?.callOptionData?.marketData?.v || 0,
        putVolume: strikeMap[s]?.putOptionData?.marketData?.v || 0,
    }))

    const res = performMarketCalculations(options as any, snap.spotPrice || snap.indexLtp || 0, {
        mode: (snap.mode as any) || 'OI',
        range: 10,
        symbol: symbol as any,
    })
    return res.supportStrike
}

/**
 * Determine VIX-based premium decay risk.
 */
function getPremiumDecayRisk(vix: number): 'HIGH' | 'MEDIUM' | 'LOW' {
    if (vix > 24) return 'HIGH'
    if (vix >= 16) return 'MEDIUM'
    return 'LOW'
}

// --- Main Algo Engine V5.2 (Quantum Refined with Strike Lows) ---

export const calculateTradeIntelligence = (input: TradeIntelligenceInput): TradeIntelligenceResult | null => {
    const { marketData, analysis, mode, history, rsiData, ceIndicators, peIndicators, ceStrikeLtp, peStrikeLtp } = input

    // console.log('[TradeIntel] Calculation Input:', { hasMarketData: !!marketData, hasAnalysis: !!analysis, optionsLen: marketData?.options?.length });

    if (!marketData || !analysis || !marketData.options) return null

    const options = marketData.options
    const spot = marketData.spotPrice || 0
    const symbol = marketData.symbol || 'NIFTY'
    const step = getStrikeStepForSymbol(symbol)

    // 1. KEY LEVELS
    const r1 = analysis.resistanceStrike || 0
    const r2 = analysis.resistanceStrike2 || r1 + 2 * step
    const s1 = analysis.supportStrike || 0
    const s2 = analysis.supportStrike2 || s1 - 2 * step

    // --- GAMMA EXPOSURE (GEX) WALLS CALCULATION ---
    // Positive GEX = Call OI * Call Gamma (Resistance)
    // Negative GEX = Put OI * Put Gamma (Support)
    let highestPosGex = 0
    let highestNegGex = 0
    let highestPosGexStrike = 0
    let highestNegGexStrike = 0

    // Fallback simple gamma proxy if API doesn't provide it (Upstox worker typically provides opt.callGamma)
    const getGammaProxy = (strike: number, spotP: number, isCall: boolean) => {
        // Gamma peaks at ATM, decays as it moves ITM/OTM. High-level approximation.
        const diff = Math.abs(strike - spotP)
        const maxGamma = 0.05 // Peak ATM gamma roughly
        return Math.max(0.001, maxGamma - (diff / spotP) * 0.5)
    }

    options.forEach((opt: any) => {
        // We natively receive IV and Greeks from the Upstox API via the worker (mapped to callGamma/putGamma)
        const ceGamma = opt.callGamma || opt.ce?.gamma || getGammaProxy(opt.strike, spot, true)
        const peGamma = opt.putGamma || opt.pe?.gamma || getGammaProxy(opt.strike, spot, false)
        const ceOI = opt.callOpenInterest || opt.ce?.oi || 0
        const peOI = opt.putOpenInterest || opt.pe?.oi || 0

        const posGex = ceOI * ceGamma
        const negGex = peOI * peGamma

        if (posGex > highestPosGex) {
            highestPosGex = posGex
            highestPosGexStrike = opt.strike
        }
        if (negGex > highestNegGex) {
            highestNegGex = negGex
            highestNegGexStrike = opt.strike
        }
    })

    const zeroGammaLevel = (highestPosGexStrike + highestNegGexStrike) / 2
    const isSpotBelowZeroGamma = spot < zeroGammaLevel

    const gexData = {
        highestPosGexStrike,
        highestNegGexStrike,
        zeroGammaLevel,
        isSpotBelowZeroGamma,
    }

    // --- DATA SUFFICIENCY CHECK ---
    // If we have less than 3 minutes of history, we cannot determine trends reliably.
    if (!history || history.length < 3) {
        return {
            score: 0,
            signalType: 'NEUTRAL',
            signalSubType: 'RANGE_BOUND',
            signalStrength: 'WEAK',
            setup: {
                type: 'WAITING',
                strategyClass: 'NEUTRAL',
                header: 'GATHERING DATA',
                strike: '—',
                entry: '—',
                sl: '—',
                target: '—',
                upside: '0 pts',
                conviction: 0,
                color: 'slate',
            },
            management: {
                action: 'WAIT',
                stopLoss: '—',
                target: '—',
                warning: 'Insufficient data for trend analysis. Waiting for market history...',
            },
            levels: { s1, s2, r1, r2, shift: analysis.shift || 0 },
            thesisPoints: [{ type: 'neutral', text: 'System initializing. Building intraday trend component...' }],
            compiledThesis:
                'System initializing. Gathering historical data to build an intraday trend component before calculating signal probability.',
            metrics: { variance: 0, pcr: marketData.pcr || 0, vix: marketData.vix || 0, totalCount: 0 },
            trendFactors: {
                shiftTrend: 'SIDEWAYS',
                varianceTrend: 'SIDEWAYS',
                countTrend: 'SIDEWAYS',
                pcrTrend: 'SIDEWAYS',
                vixTrend: 'SIDEWAYS',
                resistanceTrend: 'SIDEWAYS',
                supportTrend: 'SIDEWAYS',
                rsiDivergence: 0,
                premiumDecayRisk: 'LOW',
                shiftOldTrend: 'SIDEWAYS',
                gexRegime: 'NEUTRAL',
            },
            strikeLowData: {
                score: 0,
                netScore: 0,
                peBrokenCount: 0,
                ceAboveCount: 0,
                ceBrokenCount: 0,
                peAboveCount: 0,
                peLowsCount: 0,
                ceLowsCount: 0,
            },
            gexData: {
                highestPosGexStrike: 0,
                highestNegGexStrike: 0,
                isSpotBelowZeroGamma: false,
                zeroGammaLevel: 0,
            },
            conviction: 0,
        }
    }

    // 2. METRICS (Current Point-in-Time)
    const variance = analysis.variance?.finalVariance || 0
    // Prefer explicitly calculated counts if available, otherwise fallback to simple total
    const ceCount = analysis.variance?.ceCount || 0
    const peCount = analysis.variance?.peCount || 0
    const totalCount = analysis.variance?.totalCount || (analysis as any).totalCount || 0

    const currentShift = analysis.shift || 0
    const isSpotAboveShift = spot > currentShift
    const pcr = marketData.pcr || 1
    const vix = marketData.vix || 13

    // 3. TREND FACTORS
    const shiftSeries = extractMetricSeries(history, 'shift')
    const shiftOldSeries = extractMetricSeries(history, 'shiftOld')
    const varianceSeries = extractMetricSeries(history, 'variance')
    const countSeries = extractMetricSeries(history, 'totalCount')
    const pcrSeries = extractMetricSeries(history, 'pcr')
    const vixSeries = extractMetricSeries(history, 'vix')
    const resSeries = extractMetricSeries(history, 'resistance')
    const supSeries = extractMetricSeries(history, 'support')

    // DEBUG: Log series lengths
    // console.log( "[TradeIntel] Series lengths:", { ... } );

    // Calculate Trends with Tuned Thresholds (lowered for sensitivity)
    const shiftTrend = calculateTrend(shiftSeries, 0.1) // Very sensitive
    const shiftOldTrend = calculateTrend(shiftOldSeries, 0.1) // Very sensitive
    // V5.3: Absolute bias logic for Summary Chips (Variance & Count)
    // The user prefers the arrow to reflect the CURRENT DIRECTION/BIAS of the number, not its history.
    let varianceTrend: 'UP' | 'DOWN' | 'SIDEWAYS' = 'SIDEWAYS'
    if (variance > 0) varianceTrend = 'UP'
    else if (variance < 0) varianceTrend = 'DOWN'

    let countTrend: 'UP' | 'DOWN' | 'SIDEWAYS' = 'SIDEWAYS'
    if (totalCount > 0) countTrend = 'UP'
    else if (totalCount < 0) countTrend = 'DOWN'

    const pcrTrend = calculateTrend(pcrSeries, 1) // Sensitive to 1% change

    // V5.3: Arrow reflects actual movement (UP/DOWN), Color reflects sentiment (Bullish/Bearish)
    // Down VIX = Down Arrow (Good/Bullish), Up VIX = Up Arrow (Bad/Bearish)
    const vixTrend = calculateTrend(vixSeries, 0.2)

    // S/R Trends (Physical Migration)
    let resistanceTrend: 'UP' | 'DOWN' | 'SIDEWAYS' = 'SIDEWAYS'
    if (resSeries.length > 0) {
        const last = resSeries[resSeries.length - 1]
        const first = resSeries[0]
        if (last !== undefined && first !== undefined) {
            if (last > first) resistanceTrend = 'UP'
            else if (last < first) resistanceTrend = 'DOWN'
        }
    }

    let supportTrend: 'UP' | 'DOWN' | 'SIDEWAYS' = 'SIDEWAYS'
    if (supSeries.length > 0) {
        const last = supSeries[supSeries.length - 1]
        const first = supSeries[0]
        if (last !== undefined && first !== undefined) {
            if (last > first) supportTrend = 'UP'
            else if (last < first) supportTrend = 'DOWN'
        }
    }

    const rsiSeriesCe = rsiData
        ? history?.map((h) => (h as any).rsiData?.ceRsi || rsiData.ceRsi) || [rsiData.ceRsi]
        : []
    const rsiSeriesPe = rsiData
        ? history?.map((h) => (h as any).rsiData?.peRsi || rsiData.peRsi) || [rsiData.peRsi]
        : []
    const spotSeries = history
        .map((h) => (h as any).marketData?.spotPrice || (h as any).data?.marketData?.ltp || h.spotPrice || 0)
        .filter((v) => v > 0)

    const calculateRsiTrend = (series: number[], threshold = 2) => {
        if (series.length < 3) return 'SIDEWAYS'
        const recentAvg = series.slice(-3).reduce((a, b) => a + b, 0) / 3
        const olderAvg = series.slice(0, 3).reduce((a, b) => a + b, 0) / 3
        if (recentAvg > olderAvg + threshold) return 'UP'
        if (recentAvg < olderAvg - threshold) return 'DOWN'
        return 'SIDEWAYS'
    }

    const ceRsiTrend = calculateRsiTrend(rsiSeriesCe, 2)
    const peRsiTrend = calculateRsiTrend(rsiSeriesPe, 2)

    // Price Action Trend Analysis (Underlying Asset)
    const spotTrend = calculateRsiTrend(spotSeries, 0.5) // Tighter threshold for spot price
    const isPriceActionBullish = spotTrend === 'UP'
    const isPriceActionBearish = spotTrend === 'DOWN'

    // Advanced Confirmation: RSI momentum confirmed by actual Price Action Trend
    const isRsiConfirmedBull = ceRsiTrend === 'UP' && peRsiTrend === 'DOWN' && isPriceActionBullish
    const isRsiConfirmedBear = ceRsiTrend === 'DOWN' && peRsiTrend === 'UP' && isPriceActionBearish

    const rsiDivergence = rsiData ? rsiData.ceRsi - rsiData.peRsi : 0
    const premiumDecayRisk = getPremiumDecayRisk(vix)

    const trendFactors: TrendFactors = {
        shiftTrend,
        varianceTrend,
        countTrend,
        pcrTrend,
        vixTrend,
        resistanceTrend,
        supportTrend,
        rsiDivergence,
        premiumDecayRisk,
        shiftOldTrend,
        gexRegime:
            Math.abs(spot - zeroGammaLevel) < step * 0.1 ? 'NEUTRAL' : isSpotBelowZeroGamma ? 'VOLATILE' : 'STABLE',
    }

    // --- HELPER: STRIKE LOW LEVELS SCORING ---
    const calculateStrikeLowScore = () => {
        let sc = 0
        if (!peStrikeLtp || !ceStrikeLtp)
            return {
                score: 0,
                netScore: 0,
                peBrokenCount: 0,
                ceAboveCount: 0,
                ceBrokenCount: 0,
                peAboveCount: 0,
                peLowsCount: 0,
                ceLowsCount: 0,
            }

        const peLows = peIndicators ? [
            (peIndicators as any).levels?.low,
            (peIndicators as any).first5Min?.low,
            (peIndicators as any).first15Min?.low,
            (peIndicators as any).prevDay?.low,
            (peIndicators as any).prevWeek?.low,
            (peIndicators as any).prevMonth?.low,
        ].filter((l) => l !== undefined && l !== null && Number(l) > 0) as number[] : []

        const ceLows = ceIndicators ? [
            (ceIndicators as any).levels?.low,
            (ceIndicators as any).first5Min?.low,
            (ceIndicators as any).first15Min?.low,
            (ceIndicators as any).prevDay?.low,
            (ceIndicators as any).prevWeek?.low,
            (ceIndicators as any).prevMonth?.low,
        ].filter((l) => l !== undefined && l !== null && Number(l) > 0) as number[] : []

        // Fallback: If no indicators provided via API, calculate intraday low from history array
        if (peLows.length === 0 && ceLows.length === 0 && history && history.length > 0) {
            let histCeLow = Infinity
            let histPeLow = Infinity
            const targetStrike = analysis?.v1Strike || spot
            const symbol = marketData.symbol || 'NIFTY'
            const expiry = marketData.expiry || ''
            
            console.log('[TradeIntel] History length:', history.length, 'TargetStrike:', targetStrike)
            
            history.forEach((snap: any, index: number) => {
                const opts = extractSnapshotOptions(snap, symbol, expiry)
                if (index === 0) console.log('[TradeIntel] Snap 0 options length:', opts.length)
                
                const opt = opts.find((o: any) => Number(o.strike || o.strikePrice) === Number(targetStrike))
                if (opt) {
                    const cLtp = opt.callPrice || opt.ce?.ltp || 0
                    const pLtp = opt.putPrice || opt.pe?.ltp || 0
                    if (cLtp > 0 && cLtp < histCeLow) histCeLow = cLtp
                    if (pLtp > 0 && pLtp < histPeLow) histPeLow = pLtp
                }
            })
            console.log('[TradeIntel] Found Lows -> CE:', histCeLow, 'PE:', histPeLow)
            if (histCeLow !== Infinity) ceLows.push(histCeLow)
            if (histPeLow !== Infinity) peLows.push(histPeLow)
        }

        // Deduplicate levels (sometimes PD-Low and 15m labels overlap)
        const uniquePeLows = Array.from(new Set(peLows))
        const uniqueCeLows = Array.from(new Set(ceLows))

        // Highest low values first
        uniquePeLows.sort((a, b) => b - a)
        uniqueCeLows.sort((a, b) => b - a)

        let peBrokenCount = 0
        let ceAboveCount = 0
        let ceBrokenCount = 0
        let peAboveCount = 0

        uniquePeLows.forEach((lvl) => {
            if (peStrikeLtp < lvl) peBrokenCount++
            if (peStrikeLtp >= lvl) peAboveCount++
        })
        uniqueCeLows.forEach((lvl) => {
            if (ceStrikeLtp < lvl) ceBrokenCount++
            if (ceStrikeLtp >= lvl) ceAboveCount++
        })

        // Mathematical Net Sentiment Calculation
        // CE Not Broken (+1), CE Broken (-1) -> Call Premium holding (Bullish) vs decaying (Bearish)
        // PE Broken (+1), PE Not Broken (-1) -> Put Premium decaying (Bullish) vs holding (Bearish)
        const netScore = ceAboveCount * 1 + ceBrokenCount * -1 + peBrokenCount * 1 + peAboveCount * -1

        // Scale to a score matching our engine's weight max of 15
        // With up to 5 unique levels, max netScore is 10. *1.5 => 15
        sc = netScore * 1.5

        return {
            score: sc,
            netScore,
            peBrokenCount,
            ceAboveCount,
            ceBrokenCount,
            peAboveCount,
            peLowsCount: uniquePeLows.length,
            ceLowsCount: uniqueCeLows.length,
        }
    }

    const strikeLowData = calculateStrikeLowScore()

    // --- 4. QUANTUM SCORING ENGINE V6.0 Re-weighted ---
    // User Preference Priority:
    // 1. RSI Difference (Max 25 pts)
    // 2. Shift New and Shift trend (Max 15 pts)
    // 3. Strike Low Levels (Max 15 pts)
    // 4. Variance (Max 15 pts)
    // 5. Total Count (Max 10 pts)
    // 6. PCR (Max 10 pts)
    // 7. India VIX (Max 5 pts)
    // 8. Support and Resistance Trend (Max 10 pts)
    // 9. Support and Resistance Levels for Reversals (Max 10 pts)
    // 10. Shift OLD and Shift trend (Max 5 pts)
    // Max positive score: 120 pts

    let score = 0
    const thesisPoints: { type: 'bull' | 'bear' | 'neutral' | 'risk'; text: string }[] = []
    const arbitrageSignals: ArbitrageSignal[] = []

    // --- ARBITRAGE ENGINE V1.0 ---
    options.forEach((opt: any) => {
        const ceLtp = opt.callPrice || 0;
        const peLtp = opt.putPrice || 0;
        const k = opt.strike;
        
        // 1. Negative Extrinsic Value (Discount Arbitrage)
        // For CE: Intrinsic = Spot - Strike. If LTP < Intrinsic, it's trading at a discount.
        if (spot > k) {
            const ceIntrinsic = spot - k;
            if (ceLtp > 0 && ceLtp < ceIntrinsic * 0.98) { // 2% buffer for spread/data lag
                arbitrageSignals.push({
                    type: 'DISCOUNT_CE',
                    message: `CE Trading Below Intrinsic! Risk-Free Buy.`,
                    profitEst: (ceIntrinsic - ceLtp),
                    strike: k
                });
            }
        }
        // For PE: Intrinsic = Strike - Spot
        if (spot < k) {
            const peIntrinsic = k - spot;
            if (peLtp > 0 && peLtp < peIntrinsic * 0.98) {
                arbitrageSignals.push({
                    type: 'DISCOUNT_PE',
                    message: `PE Trading Below Intrinsic! Risk-Free Buy.`,
                    profitEst: (peIntrinsic - peLtp),
                    strike: k
                });
            }
        }

        // 2. Put-Call Parity Skew (Synthetic Future Divergence)
        // Synthetic Spot = Strike + CE LTP - PE LTP
        // We only check near the ATM (within 1 step)
        if (Math.abs(spot - k) <= step) {
            if (ceLtp > 0 && peLtp > 0) {
                const syntheticSpot = k + ceLtp - peLtp;
                const divergence = syntheticSpot - spot;
                // If divergence is highly abnormal (e.g., > 20 points for NIFTY)
                if (Math.abs(divergence) > step * 0.4) {
                    arbitrageSignals.push({
                        type: 'SYNTHETIC_SKEW',
                        message: `Extreme Put-Call Parity Skew. Synthetic Spot: ${syntheticSpot.toFixed(1)}`,
                        profitEst: Math.abs(divergence),
                        strike: k
                    });
                }
            }
        }
    });

    // --- 1. RSI Difference (Max 25 pts) ---
    // Primary Lead Indicator
    if (rsiDivergence > 12) {
        score += isRsiConfirmedBull ? 25 : 15 // Strong Bull Momentum (Smoothed)
        thesisPoints.push({
            type: 'bull',
            text: `⚡ RSI SURGE: Strong Call Side Momentum (+${rsiDivergence.toFixed(1)}) ${isRsiConfirmedBull ? '(Confirmed Trend)' : '(Wick Warning)'}`,
        })
    } else if (rsiDivergence > 5) {
        score += isRsiConfirmedBull ? 15 : 10 // Moderate Bull
        thesisPoints.push({ type: 'bull', text: `⚡ RSI UP: Positive Momentum Divergence` })
    } else if (rsiDivergence < -12) {
        score -= isRsiConfirmedBear ? 25 : 15 // Strong Bear Momentum
        thesisPoints.push({
            type: 'bear',
            text: `⚡ RSI CRASH: Strong Put Side Momentum (${rsiDivergence.toFixed(1)}) ${isRsiConfirmedBear ? '(Confirmed Trend)' : '(Wick Warning)'}`,
        })
    } else if (rsiDivergence < -5) {
        score -= isRsiConfirmedBear ? 15 : 10 // Moderate Bear
        thesisPoints.push({ type: 'bear', text: `⚡ RSI DOWN: Negative Momentum Divergence` })
    }

    // --- 2. Shift New and Shift trend (Max 15 pts) ---
    // Note: Shift represents the Approximate Index Close (Equilibrium)
    if (shiftTrend === 'UP') {
        score += 15
        thesisPoints.push({ type: 'bull', text: `📈 SHIFT MIGRATION: Approximate Index Close moving higher` })
    } else if (shiftTrend === 'DOWN') {
        score -= 15
        thesisPoints.push({ type: 'bear', text: `📉 SHIFT MIGRATION: Approximate Index Close moving lower` })
    }

    // --- 3. Strike Low Levels (Max 15 pts) ---
    // Scaled from original 20 to 15
    if (strikeLowData.score > 0) {
        score += Math.min(15, strikeLowData.score)
        const matchedLows = Math.min(strikeLowData.peBrokenCount, strikeLowData.ceAboveCount)
        thesisPoints.push({
            type: 'bull',
            text: `🎯 LOW BROKEN: Bullish - ${matchedLows} PE lows broken & CE holding above`,
        })
    } else if (strikeLowData.score < 0) {
        score += Math.max(-15, strikeLowData.score)
        const matchedLows = Math.min(strikeLowData.ceBrokenCount, strikeLowData.peAboveCount)
        thesisPoints.push({
            type: 'bear',
            text: `🎯 LOW BROKEN: Bearish - ${matchedLows} CE lows broken & PE holding above`,
        })
    }

    // --- 4. Variance (Max 15 pts) ---
    if (varianceTrend === 'UP') {
        score += variance > 5 ? 15 : 10
        thesisPoints.push({ type: 'bull', text: `🔥 VOL FLOW: Put Writers aggressive` })
    } else if (varianceTrend === 'DOWN') {
        score -= variance < -5 ? 15 : 10
        thesisPoints.push({ type: 'bear', text: `❄️ VOL FLOW: Call Writers aggressive` })
    }

    // --- 5. Total Count (Max 10 pts) ---
    if (countTrend === 'UP') {
        score += totalCount > 5 ? 10 : 5
        thesisPoints.push({ type: 'bull', text: `✅ BREADTH: Rank strength improving` })
    } else if (countTrend === 'DOWN') {
        score -= totalCount < -5 ? 10 : 5
        thesisPoints.push({ type: 'bear', text: `❌ BREADTH: Rank strength deteriorating` })
    }

    // --- 6. PCR (Max 10 pts) ---
    if (pcrTrend === 'UP' && pcr < 1.4) {
        score += 10
    } else if (pcrTrend === 'DOWN' && pcr > 0.6) {
        score -= 10
    }
    // High conviction extremes
    if (pcr > 1.4 && rsiDivergence < 0) {
        score -= 5
    } // Overbought?
    if (pcr < 0.6 && rsiDivergence > 0) {
        score += 5
    } // Oversold?

    // --- 7. India VIX (Max 5 pts) ---
    if (vixTrend === 'DOWN')
        score += 5 // Risk ON
    else if (vixTrend === 'UP') score -= 5 // Risk OFF

    // --- 8. Support and Resistance Trend (Max 10 pts) ---
    if (supportTrend === 'UP') {
        score += 10
        thesisPoints.push({ type: 'bull', text: `🛡️ LEVEL UP: Support base rising` })
    }
    if (resistanceTrend === 'DOWN') {
        score -= 10
        thesisPoints.push({ type: 'bear', text: `🧱 LEVEL DOWN: Resistance cap lowering` })
    }

    // --- 9. Support and Resistance Levels for Reversals (Max 10 pts) ---
    if (spot <= s1) {
        score += 10
        thesisPoints.push({ type: 'bull', text: `🛡️ S/R BOUNCE: Price testing Support Levels` })
    } else if (spot >= r1) {
        score -= 10
        thesisPoints.push({ type: 'bear', text: `🧱 S/R REJECT: Price testing Resistance Levels` })
    }
    // Location proximity context inside the zones
    else if (isSpotAboveShift) {
        score += 5
        thesisPoints.push({ type: 'bull', text: `🟢 LOCATION: Spot trading above Approximate Index Close (Shift)` })
    } else {
        score -= 5
        thesisPoints.push({ type: 'bear', text: `🔴 LOCATION: Spot trading below Approximate Index Close (Shift)` })
    }

    // --- 10. Shift OLD and Shift trend (Max 5 pts) ---
    if (shiftOldTrend === 'UP') {
        score += 5
        thesisPoints.push({ type: 'bull', text: `📊 OLD SHIFT: Core Approximate Index Close moving higher` })
    } else if (shiftOldTrend === 'DOWN') {
        score -= 5
        thesisPoints.push({ type: 'bear', text: `📊 OLD SHIFT: Core Approximate Index Close moving lower` })
    }

    // --- ADVANCED INSTITUTIONAL SIGNALS (V6.0) ---

    // A. Base-Time Divergence Squeeze (Smart Money Reversal)
    const isBaseTimeBearish = marketData.dayChange && marketData.dayChange < 0; // Or index is below morning open
    if (isBaseTimeBearish && variance > 10 && varianceTrend === 'UP' && countTrend === 'UP') {
        score += 40; // Massive Boost
        thesisPoints.push({ type: 'bull', text: `🧠 BASE-TIME DIVERGENCE: Spot is down, but Variance is highly POSITIVE. Smart money is buying the dip.` });
    } else if (!isBaseTimeBearish && variance < -10 && varianceTrend === 'DOWN' && countTrend === 'DOWN') {
        score -= 40;
        thesisPoints.push({ type: 'bear', text: `🧠 BASE-TIME DIVERGENCE: Spot is up, but Variance is highly NEGATIVE. Smart money is distributing calls.` });
    }

    // B. Dual-Migration Squeeze (Gamma Breakout)
    if (resistanceTrend === 'UP' && supportTrend === 'UP' && variance > 5) {
        score += 30;
        thesisPoints.push({ type: 'bull', text: `🚀 DUAL MIGRATION: Support AND Resistance migrating HIGHER. Gamma squeeze imminent.` });
    } else if (resistanceTrend === 'DOWN' && supportTrend === 'DOWN' && variance < -5) {
        score -= 30;
        thesisPoints.push({ type: 'bear', text: `🚀 DUAL MIGRATION: Support AND Resistance migrating LOWER. Put gamma squeeze imminent.` });
    }

    // C. Count-Variance Singularity (Zero-Lag Entry)
    // When Total Count flips positive exactly as Variance starts accelerating up near support
    if (totalCount > 0 && totalCount < 10 && countTrend === 'UP' && varianceTrend === 'UP' && Math.abs(spot - s1) < step) {
        score += 35;
        thesisPoints.push({ type: 'bull', text: `⏳ SINGULARITY ENTRY: Total Count just flipped Positive exactly at Support Level.` });
    } else if (totalCount < 0 && totalCount > -10 && countTrend === 'DOWN' && varianceTrend === 'DOWN' && Math.abs(spot - r1) < step) {
        score -= 35;
        thesisPoints.push({ type: 'bear', text: `⏳ SINGULARITY ENTRY: Total Count just flipped Negative exactly at Resistance Level.` });
    }

    // D. Volume/OI Conflict Trap (Liquidity Void)
    // If we have access to analysis.shiftV (Volume Shift), we can compare them.
    const volumeShift = analysis.shiftV || analysis.shift;
    if (spot > volumeShift && spot < currentShift && variance > 5) {
        score += 25;
        thesisPoints.push({ type: 'bull', text: `🕳️ LIQUIDITY VOID: Spot broke Volume resistance, but OI resistance is much higher. Clear path up.` });
    } else if (spot < volumeShift && spot > currentShift && variance < -5) {
        score -= 25;
        thesisPoints.push({ type: 'bear', text: `🕳️ LIQUIDITY VOID: Spot broke Volume support, but OI support is much lower. Clear path down.` });
    }

    // --- SIGNAL CLASSIFICATION ---
    let signalType: 'BULLISH' | 'BEARISH' | 'NEUTRAL' = 'NEUTRAL'
    let signalSubType:
        | 'BREAKOUT'
        | 'BREAKDOWN'
        | 'REVERSAL_SHARP'
        | 'REVERSAL_SLOW'
        | 'TREND_CONTINUATION'
        | 'RANGE_BOUND'
        | 'BULL_TRAP'
        | 'BEAR_TRAP'
        | 'LIQUIDITY_SWEEP' = 'RANGE_BOUND'
    let signalStrength: 'STRONG' | 'MODERATE' | 'WEAK' = 'WEAK'

    if (score >= 35) signalType = 'BULLISH'
    else if (score <= -35) signalType = 'BEARISH'
    else signalType = 'NEUTRAL'

    const absScore = Math.abs(score)
    if (absScore >= 75) signalStrength = 'STRONG'
    else if (absScore >= 50) signalStrength = 'MODERATE'
    else signalStrength = 'WEAK'

    // --- TRAP DETECTION (Updated with Liquidity Sweeps) ---
    let isTrap = false

    // To fix TS narrowing override issues, declare a completely mutable state
    let finalSignalType: 'BULLISH' | 'BEARISH' | 'NEUTRAL' = signalType
    let finalSignalSubType:
        | 'BREAKOUT'
        | 'BREAKDOWN'
        | 'REVERSAL_SHARP'
        | 'REVERSAL_SLOW'
        | 'TREND_CONTINUATION'
        | 'RANGE_BOUND'
        | 'BULL_TRAP'
        | 'BEAR_TRAP'
        | 'LIQUIDITY_SWEEP' = signalSubType as any

    // BULL TRAP: Spot Breaking Res BUT RSI Divergence is Negative
    if (spot > r1 && rsiDivergence < -5) {
        finalSignalType = 'BEARISH'
        finalSignalSubType = 'BULL_TRAP'
        score = -65 // Force Flip
        isTrap = true
        thesisPoints.push({ type: 'bear', text: `🚨 BULL TRAP: Price > Res but Momentum Negative` })
    }
    // BULL TRAP 2: Strike Lows Heavily Bearish but Price Breakout
    else if (spot > r1 && strikeLowData.score <= -12) {
        finalSignalType = 'BEARISH'
        finalSignalSubType = 'BULL_TRAP'
        score = -65 // Force Flip
        isTrap = true
        thesisPoints.push({ type: 'bear', text: `🚨 BULL TRAP: Price > Res but CE Lows aggressively broken` })
    }
    // LIQUIDITY SWEEP (BULLISH): CE Lows broke briefly, but recovered (Time-Based Rejection)
    else if (
        ceStrikeLtp &&
        ceIndicators?.first15Min?.low &&
        Math.abs(ceStrikeLtp - Number(ceIndicators.first15Min.low)) < ceStrikeLtp * 0.05 &&
        isRsiConfirmedBull
    ) {
        finalSignalType = 'BULLISH'
        finalSignalSubType = 'LIQUIDITY_SWEEP'
        score = 85 // Extreme Force Flip
        isTrap = true // Smart Money trap against retail
        thesisPoints.push({
            type: 'bull',
            text: `🌪️ LIQUIDITY SWEEP (FALSE BREAKDOWN): CE Lows probed and rejected. Smart money absorbing sellers.`,
        })
    }
    // BEAR TRAP: Spot Breaking Supp BUT RSI Divergence is Positive
    else if (spot < s1 && rsiDivergence > 5) {
        finalSignalType = 'BULLISH'
        finalSignalSubType = 'BEAR_TRAP'
        score = 65 // Force Flip
        isTrap = true
        thesisPoints.push({ type: 'bull', text: `🚨 BEAR TRAP: Price < Supp but Momentum Positive` })
    }
    // BEAR TRAP 2: Strike Lows Heavily Bullish but Price Breakdown
    else if (spot < s1 && strikeLowData.score >= 12) {
        finalSignalType = 'BULLISH'
        finalSignalSubType = 'BEAR_TRAP'
        score = 65 // Force Flip
        isTrap = true
        thesisPoints.push({ type: 'bull', text: `🚨 BEAR TRAP: Price < Supp but PE Lows aggressively broken` })
    }
    // LIQUIDITY SWEEP (BEARISH): PE Lows broke briefly, but recovered
    else if (
        peStrikeLtp &&
        peIndicators?.first15Min?.low &&
        Math.abs(peStrikeLtp - Number(peIndicators.first15Min.low)) < peStrikeLtp * 0.05 &&
        isRsiConfirmedBear
    ) {
        finalSignalType = 'BEARISH'
        finalSignalSubType = 'LIQUIDITY_SWEEP'
        score = -85 // Extreme Force Flip
        isTrap = true // Smart Money trap against retail
        thesisPoints.push({
            type: 'bear',
            text: `🌪️ LIQUIDITY SWEEP (FALSE BREAKOUT): PE Lows probed and rejected. Smart money absorbing buyers.`,
        })
    }

    // Sub-Type Logic
    if (!isTrap && finalSignalType !== 'NEUTRAL') {
        if (finalSignalType === 'BULLISH' && shiftTrend === 'DOWN') {
            finalSignalSubType = 'REVERSAL_SHARP'
        } else if (finalSignalType === 'BEARISH' && shiftTrend === 'UP') {
            finalSignalSubType = 'REVERSAL_SHARP'
        } else {
            finalSignalSubType = 'TREND_CONTINUATION'
        }
    } else if (!isTrap) {
        finalSignalSubType = 'RANGE_BOUND'
    }

    signalType = finalSignalType
    signalSubType = finalSignalSubType

    const conviction = Math.min(100, Math.round((Math.abs(score) / 100) * 100))

    // 6. SETUP GENERATION
    let setup = null
    const targetCeStrike = ceStrikeLtp && ceStrikeLtp > 0 ? analysis.resistanceStrike : Math.round(spot / step) * step
    const targetPeStrike = peStrikeLtp && peStrikeLtp > 0 ? analysis.supportStrike : Math.round(spot / step) * step
    const targetStrike = finalSignalType === 'BEARISH' ? targetPeStrike : targetCeStrike
    let activeTarget = 0
    let activeSL = 0

    // Ensure we have final LTPs if UI missed them
    const finalCeStrikeLtp = ceStrikeLtp || marketData?.options?.find((o) => o.strike === targetCeStrike)?.callPrice
    const finalPeStrikeLtp = peStrikeLtp || marketData?.options?.find((o) => o.strike === targetPeStrike)?.putPrice

    const calculateDynamicPremiumTargets = (ltp: number | undefined, isBull: boolean, indicators?: any) => {
        if (!ltp) return { entry: 'CMP', sl: '—', target: '—', upside: '—' }

        let risk = ltp * 0.15 // default 15% risk
        let reward = ltp * 0.3 // default 30% target
        let sl = ltp - risk
        let target = ltp + reward

        // Advanced calculation using Candlestick History Structural Lows/Highs
        if (indicators && indicators.first15Min && indicators.pmLimit) {
            const historyLow = Math.min(Number(indicators.pmLimit.low), Number(indicators.first15Min.low))
            const historyHigh = Math.max(Number(indicators.pmLimit.high), Number(indicators.first15Min.high))

            // Validate historical structure limits vs current LTP before using them
            if (historyLow > 0 && ltp > historyLow && ltp - historyLow < ltp * 0.35) {
                sl = historyLow * 0.98 // SL 2% below the core structural low
            }
            if (historyHigh > ltp && historyHigh - ltp < ltp * 0.8) {
                target = historyHigh * 1.05 // Target 5% above structural high
            }
        }

        return {
            entry: `CMP ${ltp.toFixed(1)} / Lmt ${(ltp * 0.95).toFixed(1)}`, // Small entry limit zone
            sl: Math.max(0, sl).toFixed(1),
            target: target.toFixed(1),
            upside: `+${(target - ltp).toFixed(1)} pts`,
        }
    }

    if (
        (finalSignalType as string) === 'BULLISH' ||
        (finalSignalSubType as string) === 'BEAR_TRAP' ||
        ((finalSignalType as string) === 'BULLISH' && (finalSignalSubType as string) === 'LIQUIDITY_SWEEP')
    ) {
        // Standard Bullish
        // If we break R1, Target is R2. If we are at Support, Target is Shift or R1.
        if (spot > r1) {
            activeTarget = r2
            activeSL = r1
        } else if (spot > currentShift) {
            activeTarget = r1
            activeSL = currentShift
        } else {
            activeTarget = currentShift
            activeSL = s1
        }

        // Sanity Check: If Target is too close (< 0.2% away), push to next level
        if (activeTarget <= spot * 1.001) activeTarget += step
        if (activeSL >= spot * 0.999) activeSL -= step // SL must be below

        const dynamic = finalCeStrikeLtp ? calculateDynamicPremiumTargets(finalCeStrikeLtp, true, ceIndicators) : null

        setup = {
            type: 'BUY CALL',
            strategyClass:
                (signalSubType as string).includes('TRAP') || (signalSubType as string).includes('SWEEP')
                    ? 'TRAP / SWEEP'
                    : 'DEBIT',
            header: (signalSubType as string).replace('_', ' '),
            strike: `${targetCeStrike} CE`,
            entry: dynamic?.entry || `CMP (${spot.toFixed(0)})`,
            sl: dynamic?.sl || `${activeSL}`,
            target: dynamic?.target || `${activeTarget}`,
            upside: dynamic?.upside || `+${Math.max(0, activeTarget - spot).toFixed(0)} pts`,
            conviction: isTrap ? 85 : conviction,
            color: 'emerald',
        }
    } else if (
        (finalSignalType as string) === 'BEARISH' ||
        (finalSignalSubType as string) === 'BULL_TRAP' ||
        ((finalSignalType as string) === 'BEARISH' && (finalSignalSubType as string) === 'LIQUIDITY_SWEEP')
    ) {
        // Standard Bearish
        // If we break S1, Target is S2. If we are at Resistance, Target is Shift or S1.
        if (spot < s1) {
            activeTarget = s2
            activeSL = s1
        } else if (spot < currentShift) {
            activeTarget = s1
            activeSL = currentShift
        } else {
            activeTarget = currentShift
            activeSL = r1
        }

        // Sanity Check: If Target is too close (or above), push lower
        if (activeTarget >= spot * 0.999) activeTarget -= step
        if (activeSL <= spot * 1.001) activeSL += step // SL must be above

        const dynamic = finalPeStrikeLtp ? calculateDynamicPremiumTargets(finalPeStrikeLtp, false, peIndicators) : null

        setup = {
            type: 'BUY PUT',
            strategyClass:
                (signalSubType as string).includes('TRAP') || (signalSubType as string).includes('SWEEP')
                    ? 'TRAP / SWEEP'
                    : 'DEBIT',
            header: (signalSubType as string).replace('_', ' '),
            strike: `${targetPeStrike} PE`,
            entry: dynamic?.entry || `CMP (${spot.toFixed(0)})`,
            sl: dynamic?.sl || `${activeSL}`,
            target: dynamic?.target || `${activeTarget}`,
            upside: dynamic?.upside || `+${Math.max(0, spot - activeTarget).toFixed(0)} pts`,
            conviction: isTrap ? 85 : conviction,
            color: 'rose',
        }
    } else {
        // NEUTRAL - Show meaningful zone information instead of dashes
        const zoneWidth = Math.abs(r1 - s1)
        setup = {
            type: 'WAIT',
            strategyClass: 'NEUTRAL',
            header: 'RANGE BOUND',
            strike: `${s1} - ${r1}`,
            entry: `${currentShift}`,
            sl: `${s1}`,
            target: `${r1}`,
            upside: `${zoneWidth} pts`,
            conviction: Math.max(10, 50 - Math.abs(score)), // Low conviction for neutral
            color: 'indigo',
        }
    }

    // 7. MANAGEMENT
    const management: TradeManagement = {
        action: 'WAIT',
        stopLoss: activeSL > 0 ? activeSL.toFixed(0) : '—',
        target: activeTarget > 0 ? activeTarget.toFixed(0) : '—',
        warning: undefined,
    }

    if (finalSignalType !== 'NEUTRAL' || isTrap) {
        management.action = isTrap ? 'CAUTION' : 'HOLD'

        if (isTrap) {
            management.warning = 'TRAP DETECTED: Momentum contradicts Price.'
        } else if (finalSignalType === 'BULLISH') {
            if (rsiDivergence < 5) management.warning = 'Momentum weak relative to price.'
            if (absScore < 60) management.action = 'CAUTION'
        } else if (finalSignalType === 'BEARISH') {
            if (rsiDivergence > -5) management.warning = 'Momentum weak relative to price.'
            if (absScore < 60) management.action = 'CAUTION'
        }
    }

    // Compile Holistic Trade Thesis
    let compiledThesis = `The market is currently showing a ${signalStrength.toLowerCase()} ${finalSignalType.toLowerCase()} bias. `
    if (isTrap) {
        compiledThesis += `However, a structural trap has been detected: the system is identifying a ${finalSignalSubType.replace('_', ' ').toLowerCase()}. Smart money is likely absorbing liquidity against retail flow. `
    } else if (finalSignalType !== 'NEUTRAL') {
        compiledThesis += `This setup is categorized as a ${finalSignalSubType.replace('_', ' ').toLowerCase()}. `
    }
    compiledThesis += `\nWhy this trade works:\n`
    thesisPoints.forEach((tp) => {
        compiledThesis += `• ${tp.text}\n`
    })

    return {
        score,
        signalType,
        signalSubType,
        signalStrength,
        setup,
        management,
        levels: { s1, s2, r1, r2, shift: currentShift },
        thesisPoints,
        compiledThesis,
        metrics: { variance, pcr, vix, totalCount },
        trendFactors,
        strikeLowData,
        gexData,
        arbitrageSignals: arbitrageSignals.length > 0 ? arbitrageSignals : undefined,
        conviction,
    }
}
